<template>
  <section class="customers-form">
    <template v-if="!data.simulation_uuid && !data.simulation_uuids">
      <typography-heading class="customers-form-title">
        Personalização

        <template v-slot:subtitle>
          Deseja que o formulário mostre apenas os campos de algum banco específico?
        </template>
      </typography-heading>

      <form-field
        class="customers-form-field"

        type="select"
        name="field._ID"

        label="Banco"
        placeholder="Escolha um banco"

        :options="options"
        :value="bankCode"

        @input="bankUpdate"
      />
    </template>

    <embed-frame
      v-if="pf"
      class="customers-form-frame"
      endpoint="customers/form"
      placeholder="Carregando..."
      :settings="embedSettings"
      :callbacks="embedCallbacks"
    />

    <customers-former
      v-else
      class="customers-form-former"
    />
  </section>
</template>

<script>
  import { mapState } from 'vuex';

  import BANKS from '@sdk/helpers/domains/banks';

  import invalidateCNPJ from '@cordel/helpers/form/validators/cnpj';

  import EmbedFrame from '@sdk/views/Embed/Frame.vue';
  import FormField from '@cordel/components/Form/Field.vue';
  import TypographyHeading from '@cordel/components/Typography/Heading.vue';

  // eslint-disable-next-line import/no-extraneous-dependencies
  import CustomersFormer from '@/views/Customers/Form.vue';

  const ALL = '_all';

  export default {
    name: 'CustomersForm',
    components: {
      EmbedFrame,
      FormField,
      TypographyHeading,

      CustomersFormer,
    },
    data() {
      return {
        customer: {},
        successCustomerID: null,

        bankCode: this.$route.query.bank_code || ALL,
      };
    },
    computed: {
      ...mapState([
        'auth',
      ]),

      pf() {
        if (this.customer.id) {
          return !!this.customer.cpf;
        }

        const cpf = this.data.cpf || this.data.cnpj || this.data.cpf_cnpj;

        return !cpf || invalidateCNPJ(cpf);
      },

      options() {
        return [{
          label: 'Mostrar campos de todos os bancos',
          value: ALL,
        }].concat(Object.values(BANKS).map(bank => ({
          label: bank.name,
          value: bank.code,
        })));
      },

      data() {
        return this.$route.query;
      },

      embedSettings() {
        const {
          store_id: storeID,

          customer_id: customerID,
          cpf: customerCPF,

          proposal,
          proposal_id: proposalID,
          proposal_attempt_id: attemptID,

          bank_code: fallbackBankCodes,
          bank_codes: bankCodes,

          simulation_uuid: simulationUUID,
          simulation_uuids: simulationUUIDs,

          condition_ids: conditionIDs,
        } = this.data;

        const editID = this.$route.params.id;

        let banks = bankCodes || fallbackBankCodes || this.bankCode;

        if (banks === ALL) {
          banks = Object.values(BANKS).map(bank => bank.code);
        }

        const settings = {
          bank_codes: banks,

          id: editID,
          cpf: customerCPF || (proposal && proposal.customer && proposal.customer.cpf) || null,
          _cpf: { readonly: !customerID, visibility: true },

          customer_id: customerID,

          _enable_optional_fields: true,
          _enable_qrcode_uploads: true,
          _enable_guarantor: !editID && !customerID && conditionIDs,

          _proposal: {
            store_id: storeID,

            id: proposalID,
            proposal_attempt_id: attemptID,

            condition_ids: conditionIDs,
          },
        };

        if (simulationUUIDs) {
          // eslint-disable-next-line no-underscore-dangle
          settings._proposal.simulation_uuids = simulationUUIDs;
        } else {
          // eslint-disable-next-line no-underscore-dangle
          settings._proposal.simulation_uuid = simulationUUID;
        }

        return {
          customers: {
            form: settings,
          },
        };
      },
      embedCallbacks() {
        return {
          'customer:read': this.customerRead,

          'customer:create': this.customerSuccess,
          'customer:update': this.customerSuccess,

          'proposal:create': this.proposalSuccess,
          'proposal_attempt:create': this.proposalSuccess,
          'proposal_attempt:update': this.proposalSuccess,
        };
      },
    },
    mounted() {
      if (!this.pf && this.$route.query.cpf) {
        this.$router.replace({
          name: this.$route.name,
          query: {
            ...this.$route.query,
            cpf_cnpj: this.data.cpf,
          },
        });
      }
    },
    methods: {
      bankUpdate(value) {
        this.bankCode = value;
      },

      customerRead(data) {
        this.customer = data.customer;
      },
      customerSuccess(data) {
        const {
          store_id: storeID,

          simulation_uuids: simulationUUIDs,
          simulation_uuid: simulationUUID,

          condition_ids: conditionIDs,
        } = this.data;

        if (simulationUUIDs || simulationUUID) {
          if (conditionIDs) {
            return;
          }

          if (data.settings && data.settings.hierarchy === 'secondary') {
            this.successCustomerID = data.customer.id;

            return;
          }

          const query = {
            integrated: false,

            store_id: storeID,
            customer_id: this.successCustomerID || data.customer.id,

            simulation_uuid: simulationUUIDs ? simulationUUIDs.split(',')[0] : simulationUUID,
          };

          this.$router.push({
            name: 'proposals-new',
            query,
          });

          return;
        }

        this.$router.push({
          name: 'customers-read-informations',
          params: {
            id: this.successCustomerID || data.customer.id,
          },
        });
      },
      proposalSuccess(data) {
        this.$router.push({
          name: 'proposals-read',
          params: {
            id: this.$route.query.proposal_id || data.complete_proposal.id,
          },
        });
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import '@cordel/assets/stylesheets/mixins/reset.scss';
  @import '@cordel/assets/stylesheets/variables/breakpoints.scss';

  .customers-form {
    padding: 0 var(--product--pwa--horizontal);

    @media all and (max-width: $cordel-breakpoint-mobile) {
      padding: 0 var(--product--pwa--horizontal);
    }
  }

  .customers-form-field {
    margin: var(--cordel-spacing-giga) 0;
  }

  .customers-form-former {
    margin-left: calc(var(--cordel-spacing-blaster) * -1);
    margin-right: calc(var(--cordel-spacing-blaster) * -1);

    @media all and (max-width: $cordel-breakpoint-mobile) {
      margin-left: calc(var(--cordel-spacing-s) * -1);
      margin-right: calc(var(--cordel-spacing-s) * -1);
    }
  }
</style>
